import { sanitiseLabelOrder } from '../pedigree-tool/pedigree/labelDefinitions';
import { sanitiseValueSort } from '../pedigree-tool/pedigree/labelValueSort';
import { sanitiseValueFilter } from '../pedigree-tool/pedigree/labelValueFilter';

// The `settings` blob a saved preset carries, alongside its per-label `overrides`.
//
// Extracted so the SHAPE is assertable (TGCLOUD-1630): the KB generator's grounding notes
// state which chart-wide options a preset does and does not store, and that claim had
// already gone stale once. A test pins these keys against the note rather than a human
// remembering to update prose when the object changes.
//
// `settings` is spread wholesale into `labelsSet` on load, which is why a new chart-wide
// switch needs no schema change and no resolve-path change to become preset-backed.
//
// Deliberately NOT carrying the other chart-wide switches (showCarrierMarks,
// showAnalysisOverlay, standardNomenclature, showRecordStatus): those are per-session view
// state, not part of a saved label configuration.
//
// TGCLOUD-1663 added `labelOrder` (the order the label rows stack) and `valueSort` (how the
// several values of one label type are ordered). Both live here rather than in the per-label
// `overrides` because they are WHOLE-collection properties, and the sparse per-key patching
// `overrides` uses cannot merge them coherently: half a stored order is not an order, and a
// sort map merged across three scopes would leave a clinician unable to say what their own
// preset does. TGCLOUD-1792 added `valueFilter` for the same reason: which records a label
// prints is a whole-collection property of that label, not a per-key patch.
//
// Note the existing per-entity legend maps (diseasesMap / phenotypesMap / genotypesMap) are
// NOT here and are therefore session-only — they come from the legend modal, which persists
// nothing. `valueFilter` is deliberately preset-backed instead: a site that wants "latest
// three tests only" wants it on every chart, not re-ticked every time.
//
// Lives in label-modal/ rather than in the old sidebar section, which TGCLOUD-1674 deleted.
export const PRESET_SETTINGS_KEYS = [
  'displayLabelMode',
  'carrierMarkStyle',
  'probandOnlyLabels',
  'labelOrder',
  'valueSort',
  'valueFilter',
  'showFamilyNote'
];

export const presetSettingsFrom = (labelsSet, displayLabelMode) => ({
  displayLabelMode,
  carrierMarkStyle: labelsSet?.carrierMarkStyle || 'border',
  // TGCLOUD-1465 — the de-identified copy is meant to be REUSABLE ("save it as a reusable
  // display preset"), and at System/Group scope this lets a site make it the default.
  probandOnlyLabels: !!labelsSet?.probandOnlyLabels,
  // TGCLOUD-1791 — whether the family note prints on the chart. Preset-backed
  // rather than per-session for the same reason probandOnlyLabels is: it is a
  // choice about what the exported document contains, which a site makes once.
  showFamilyNote: !!labelsSet?.showFamilyNote,
  // Sanitised on the way OUT as well as on the way in, so a preset can never store a corrupt
  // order even if the working state somehow held one.
  labelOrder: sanitiseLabelOrder(labelsSet?.labelOrder),
  valueSort: sanitiseValueSort(labelsSet?.valueSort),
  valueFilter: sanitiseValueFilter(labelsSet?.valueFilter)
});
